// src/AffiliateDashboard.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import { DollarSign, Users, TrendingUp, Copy, CheckCircle,
         Eye, Calendar, Download, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import {
  getAffiliateData, getAffiliateReferrals, getAffiliateCommissions,
  requestWithdrawal, COMMISSION_RATES
} from './AffiliateSystem';

const AffiliateDashboard = ({ currentUser }) => {
  const [affiliateData, setAffiliateData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [paymentDetails, setPaymentDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    if (!currentUser?.referralCode) { setLoading(false); return; }
    setLoading(true);
    try {
      const [dataRes, referralsRes, commissionsRes] = await Promise.all([
        getAffiliateData(currentUser.referralCode),
        getAffiliateReferrals(currentUser.referralCode),
        getAffiliateCommissions(currentUser.referralCode),
      ]);

      if (dataRes.success) setAffiliateData(dataRes.data);
      if (referralsRes.success) setReferrals(referralsRes.referrals);
      if (commissionsRes.success) setCommissions(commissionsRes.commissions);

      // Load withdrawals directly from Firebase
      const { getDocs, collection, query, where } = await import('firebase/firestore');
      const { db } = await import('./firebase');
      const wSnap = await getDocs(
        query(collection(db, 'withdrawals'),
              where('affiliateCode', '==', currentUser.referralCode))
      );
      setWithdrawals(wSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt)));
    } catch (e) {
      console.error('Error loading affiliate data:', e);
    }
    setLoading(false);
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(affiliateData?.referralLink || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWithdrawal = async () => {
    const amount = parseFloat(withdrawalAmount);
    if (!amount || amount < 50) return alert('Minimum withdrawal is $50');
    
    const available = affiliateData?.availableBalance || 0;
    if (amount > available) return alert(`Insufficient balance. Available: $${available.toFixed(2)}`);
    if (!paymentDetails.trim()) return alert('Please enter payment details');

    setSubmitting(true);
    const result = await requestWithdrawal(
      currentUser.referralCode, amount, paymentMethod, paymentDetails
    );
    setSubmitting(false);

    if (result.success) {
      alert('✅ Withdrawal request submitted!\n\nWe will process it within 3-5 business days.');
      setShowWithdrawal(false);
      setWithdrawalAmount('');
      setPaymentDetails('');
      await loadAllData(); // Refresh all data
    } else {
      alert('Failed: ' + result.error);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600"></div>
    </div>
  );

  if (!affiliateData) return (
    <div className="text-center p-12 bg-white rounded-xl shadow">
      <p className="text-gray-600 text-lg">No affiliate data found.</p>
      <button onClick={loadAllData} className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm">
        Retry
      </button>
    </div>
  );

  const available = affiliateData.availableBalance || 0;
  const pending = affiliateData.pendingEarnings || 0;
  const pendingWithdrawal = affiliateData.pendingWithdrawal || 0;
  const withdrawn = affiliateData.withdrawnEarnings || 0;
  const conversionRate = affiliateData.clicks > 0
    ? ((affiliateData.conversions / affiliateData.clicks) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1">Affiliate Dashboard</h1>
        <p className="text-sm text-gray-600">Track your referrals and earnings</p>
      </div>

      {/* Stats - 2x2 on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs text-gray-500 mb-1">Available to Withdraw</p>
              <p className="text-2xl font-bold text-gray-800">${available.toFixed(2)}</p>
              {pendingWithdrawal > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  ${pendingWithdrawal.toFixed(2)} pending payout
                </p>
              )}
            </div>
            <DollarSign className="w-8 h-8 flex-shrink-0 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-yellow-500">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs text-gray-500 mb-1">Pending Approval</p>
              <p className="text-2xl font-bold text-gray-800">${pending.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">Awaiting admin</p>
            </div>
            <Clock className="w-8 h-8 flex-shrink-0 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Referrals</p>
              <p className="text-2xl font-bold text-gray-800">{affiliateData.totalReferrals || 0}</p>
              <p className="text-xs text-gray-400 mt-1">{affiliateData.activeReferrals || 0} active</p>
            </div>
            <Users className="w-8 h-8 flex-shrink-0 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Earned</p>
              <p className="text-2xl font-bold text-gray-800">
                ${(affiliateData.totalEarnings || 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-400 mt-1">${withdrawn.toFixed(2)} paid out</p>
            </div>
            <TrendingUp className="w-8 h-8 flex-shrink-0 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Your Referral Link</h2>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            type="text"
            value={affiliateData.referralLink || ''}
            readOnly
            className="flex-1 px-3 py-2.5 border-2 border-gray-300 rounded-lg bg-gray-50 font-mono text-xs sm:text-sm min-w-0"
          />
          <button
            onClick={copyReferralLink}
            className="flex-shrink-0 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2 text-sm font-medium"
          >
            {copied ? <><CheckCircle className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: Eye, label: 'Clicks', value: affiliateData.clicks || 0 },
            { icon: Users, label: 'Signups', value: affiliateData.conversions || 0 },
            { icon: CheckCircle, label: 'Active', value: affiliateData.activeReferrals || 0 },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="bg-gray-50 p-3 rounded-lg text-center">
              <Icon className="w-5 h-5 text-gray-500 mx-auto mb-1" />
              <p className="text-xs text-gray-600 mb-1">{label}</p>
              <p className="text-xl font-bold text-gray-800">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Commission Rates */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Commission Rates</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className="border-2 border-green-200 bg-green-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-800 mb-2 text-sm">Review Plans</p>
            <p className="text-sm text-gray-600">Monthly: ${COMMISSION_RATES.REVIEW_PLAN_MONTHLY}</p>
            <p className="text-sm text-gray-600">Yearly: ${COMMISSION_RATES.REVIEW_PLAN_YEARLY}</p>
          </div>
          <div className="border-2 border-purple-200 bg-purple-50 p-4 rounded-lg">
            <p className="font-semibold text-gray-800 mb-2 text-sm">Promotion Plans</p>
            <p className="text-sm text-gray-600">Monthly: ${COMMISSION_RATES.PROMOTION_PLAN_MONTHLY}</p>
            <p className="text-sm text-gray-600">Yearly: ${COMMISSION_RATES.PROMOTION_PLAN_YEARLY}</p>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
          <p className="text-xs sm:text-sm text-blue-800">
            <strong>Recurring Commission:</strong> You earn {COMMISSION_RATES.LIFETIME * 100}% on ALL future purchases by your referrals!
          </p>
        </div>
      </div>

      {/* Withdraw Section */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Withdraw Earnings</h2>
            <p className="text-sm text-gray-500">
              Available: <span className="font-bold text-green-600">${available.toFixed(2)}</span>
              {pendingWithdrawal > 0 && (
                <span className="ml-2 text-orange-600">(${pendingWithdrawal.toFixed(2)} pending)</span>
              )}
            </p>
          </div>
          <button
            onClick={() => setShowWithdrawal(!showWithdrawal)}
            disabled={available < 50}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            Request Withdrawal
            {showWithdrawal ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {available < 50 && (
          <p className="text-xs text-orange-600 mb-4 bg-orange-50 p-3 rounded-lg border border-orange-200">
            Minimum withdrawal is $50. Available balance: ${available.toFixed(2)}
          </p>
        )}

        {showWithdrawal && (
          <div className="border-2 border-gray-200 rounded-xl p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Amount ($) — Max: ${available.toFixed(2)}</label>
              <input
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                min="50"
                max={available}
                className="w-full px-3 py-2.5 border rounded-lg text-sm"
                placeholder="Min $50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2.5 border rounded-lg text-sm"
              >
                <option value="bank">Bank Transfer</option>
                <option value="paypal">PayPal</option>
                <option value="crypto">Cryptocurrency</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {paymentMethod === 'bank' ? 'Bank Account Details' : paymentMethod === 'paypal' ? 'PayPal Email' : 'Crypto Wallet Address'}
              </label>
              <textarea
                value={paymentDetails}
                onChange={(e) => setPaymentDetails(e.target.value)}
                rows="3"
                className="w-full px-3 py-2.5 border rounded-lg text-sm"
                placeholder={paymentMethod === 'bank' ? 'Account Number, Bank Name, Sort Code...' : paymentMethod === 'paypal' ? 'your@email.com' : 'Wallet address'}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleWithdrawal}
                disabled={submitting}
                className="flex-1 bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 transition font-semibold text-sm disabled:bg-gray-400"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
              <button
                onClick={() => setShowWithdrawal(false)}
                className="flex-1 sm:flex-none border-2 border-gray-300 px-5 py-2.5 rounded-lg hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Withdrawal History */}
        {withdrawals.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-bold text-gray-700 mb-3">Withdrawal History</h3>
            <div className="space-y-2">
              {withdrawals.map(w => (
                <div key={w.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div>
                    <p className="text-sm font-medium text-gray-800">${w.amount?.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">{w.paymentMethod} • {new Date(w.requestedAt).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    w.status === 'completed' ? 'bg-green-100 text-green-700' :
                    w.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {w.status?.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recent Referrals */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">
          Recent Referrals ({referrals.length})
        </h2>
        {referrals.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">No referrals yet. Share your link to get started!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {referrals.slice(0, 10).map((referral) => (
              <div key={referral.id} className="border border-gray-200 rounded-xl p-3 hover:border-indigo-300 transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-800 text-sm truncate">{referral.referredUserName}</p>
                    <p className="text-xs text-gray-500 truncate">{referral.referredUserEmail}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Joined: {new Date(referral.signupAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                      referral.status === 'active' ? 'bg-green-100 text-green-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {referral.status?.toUpperCase()}
                    </span>
                    <p className="text-sm font-bold text-green-600 mt-1">
                      ${(referral.totalCommissionEarned || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Commissions */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">
          Commissions ({commissions.length})
        </h2>
        {commissions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">No commissions yet. Commissions are earned when your referrals subscribe.</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {commissions.slice(0, 10).map((c) => (
                <div key={c.id} className="border border-gray-200 rounded-xl p-3">
                  <div className="flex justify-between items-start mb-1">
                    <p className="text-sm font-medium text-gray-800">
                      {c.subscriptionType?.replace('_', ' ').toUpperCase()}
                    </p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      c.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {c.status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-xs text-gray-500">{new Date(c.createdAt).toLocaleDateString()}</p>
                    <p className="text-sm font-bold text-green-600">${c.amount?.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-600 font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-gray-600 font-medium">Type</th>
                    <th className="text-left py-3 px-4 text-gray-600 font-medium">Amount</th>
                    <th className="text-left py-3 px-4 text-gray-600 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.slice(0, 10).map((c) => (
                    <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-600">{new Date(c.createdAt).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-gray-800">{c.subscriptionType?.replace('_', ' ').toUpperCase()}</td>
                      <td className="py-3 px-4 font-bold text-green-600">${c.amount?.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          c.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {c.status?.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default AffiliateDashboard;